import {FormUtil} from "./forms/formUtil";
import {ApiErrorResponse, ErrorResponse} from "@/shared/timr";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {ElementSelector, mergeProperties, saveJQuerySelector} from "@/shared/utils/misc";
import {enhanceCopyable} from "../../resources/static/javascript/copy-button.js";
import type {KnownRouteUrl, RouteParams, RouteResponse, SupportsMethod} from "@/shared/api/routesHelper";

const baseUrl = "/timr";

type KnownApiUrl<U extends string> =
	Omit<ApiUrl<U>, "get" | "post" | "put" | "postForm">
	& (SupportsMethod<U, "GET"> extends true ? { get: ApiUrl<U>["get"] } : unknown)
	& (SupportsMethod<U, "POST"> extends true ? { post: ApiUrl<U>["post"]; postForm: ApiUrl<U>["postForm"] } : unknown)
	& (SupportsMethod<U, "PUT"> extends true ? { put: ApiUrl<U>["put"] } : unknown);

/**
 * Creates an ApiUrl instance for the given URL.
 *
 * When the URL matches a registered route in {@link ApiRoutes}, the response types for
 * get(), post(), and put() are inferred automatically. Unregistered routes fall back to `any`.
 *
 * @param url The URL to be used for the API call. It can be a relative (without leading slash) or absolute URL (with leading slash).
 * @param background If true, the request will be made in the background and no error dialog will be shown on network errors.
 * @param absolute If true, the URL will be treated as absolute and prefixed with the base URL even if it does not start with a slash. This is for legacy calls that do not have a leading slash yet. This parameter is thought to be removed in the future.
 */
export function api<U extends string>(url: KnownRouteUrl<U>, background?: boolean, absolute?: boolean): KnownApiUrl<U>;
export function api<U extends string>(url: U, background?: boolean, absolute?: boolean): ApiUrl<U>;
export function api<U extends string>(url: U, background: boolean = false, absolute: boolean = true): ApiUrl<U> {
	if (absolute && !url.startsWith('/')) {
		// migration scenario for legacy calls without leading slash
		return new ApiUrl<U>(baseUrl + "/" + url, background);
	} else if (url.startsWith('/')) {
		// leading slash means absolute URL
		return new ApiUrl<U>(baseUrl + url, background);
	} else {
		// not 'absolute' and no leading slash means relative URL
		return new ApiUrl<U>(url, background);
	}
}

export interface FormSerializationOptions {
	serializeDisabled: boolean;
}

/**
 * A request parameter as a name/value pair — the in-memory model for query/body params sent
 * via {@link ApiUrl} or jQuery ajax. A widened {@link JQuery.NameValuePair} (whose `value` is
 * `string`-only), used both for table filters (`BasicDataTable.filter`) and plain api requests.
 *
 * `value` may be any scalar: stringification happens once at the wire boundary in jQuery's
 * `$.param`, which serializes each value as `encodeURIComponent(value == null ? "" : value)`.
 * So `null`/`undefined` become `""` and `number`/`boolean` are stringified exactly like
 * `String(value)`. Pass the natural value — do not pre-coerce with `String(...)` or `|| ""`.
 */
export type RequestParam = { name: string, value: string | number | boolean | null | undefined };

export class ApiUrl<U extends string = string> {
	private static networkErrorModal ?: BootstrapModal.Modal;

	public readonly url: string;
	public readonly background: boolean;
	private settings: JQueryAjaxSettings;

	constructor(url: string, background: boolean = false) {
		this.url = url;
		this.background = background;
	}

	public options(settings: JQueryAjaxSettings): this {
		this.settings = settings;
		return this;
	}

	public get(params?: RouteParams<U, "GET">): JQuery.jqXHR<RouteResponse<U, "GET">> {
		return this.ajax("GET", params ?? {});
	}

	public post(data?: RouteParams<U, "POST">): JQuery.jqXHR<RouteResponse<U, "POST">> {
		return this.ajax("POST", data ?? {});
	}

	public put(data?: RouteParams<U, "PUT">): JQuery.jqXHR<RouteResponse<U, "PUT">> {
		return this.ajax("PUT", data ?? {});
	}

	public postForm(formSelector: ElementSelector, options?: FormSerializationOptions, additionalData?: JQuerySerializeArrayElement[]): JQuery.jqXHR<RouteResponse<U, "POST">> {
		const form = saveJQuerySelector(formSelector);

		FormUtil.loading(form);

		let data = form.serializeArray();

		if (options && options.serializeDisabled) {
			data = data.concat(FormUtil.serializeArrayDisabled(form));
		}

		if (additionalData) {
			data = data.concat(additionalData);
		}

		return this.ajax("POST", data)
			.fail(function (response: ErrorResponse) {
				if (response.responseJSON?.fieldErrors != null) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			})
			.done(function () {
				FormUtil.removeAllFeedback(form);
			})
			.always(function () {
				FormUtil.resetLoading(form);
			});
	}

	private ajax(method: "GET" | "POST" | "PUT", data: JQueryAjaxSettings["data"]): JQueryXHR {
		const ajaxSettings: JQueryAjaxSettings = mergeProperties<JQueryAjaxSettings, JQueryAjaxSettings[]>({
			url: this.url,
			method: method,
			data: data,
			 // If the frontend makes a .json request but the session is expired, the server responds with the HTML of the login page.
			 // We force the type for .json endpoints so that this counts as a parsererror, otherwise it would count as a normal
			 // success. For every other request, we keep using the auto-detection.
			 // See https://api.jquery.com/jQuery.ajax/
			dataType: this.url.endsWith(".json") ? "json" : undefined,
			cache: false,
			dataFilter: (responseData: string | undefined, dataType: string) => {
				// RFC 8259 defines a JSON text as a serialized value; an empty string is not valid JSON.
				// Some legacy *.json endpoints return 200 with an empty body on success, so normalize to `null`.
				// This is run before the dataType parsing, so this prevents parser errors on empty responses.
				// Spec reference: RFC 8259, Section 2 (JSON Grammar) https://www.rfc-editor.org/rfc/rfc8259#section-2
				if (dataType === "json" && responseData?.trim() === "") {
					return "null";
				}

				return responseData;
			},
			error: (jqXHR, textStatus) => {
				if (textStatus === "parsererror" && ApiUrl.isLoginPage(jqXHR)) {
					// Redirect to login page if server returned the login page HTML. See comment above about dataType.
					window.location.href = "/timr/";
					return;
				}

				ApiUrl.hideNetworkErrorModalIfNecessary(jqXHR);

				const errorResponse = jqXHR.responseJSON as ApiErrorResponse | undefined;
				if (jqXHR.status === 0 && jqXHR.statusText === "abort") {
					// ignore aborted requests
				} else if (jqXHR.status === 0 && jqXHR.statusText !== "abort") {
					// Status 0 is a network problem, abort means aborted by user/code
					ApiUrl.showNetworkErrorModal(this.background);
				} else if (jqXHR.status === 403 && method === "GET") {
					BootstrapModal.error({
						title: "httpStatus.forbidden.title",
						message: ApiUrl.buildErrorDialogMessage("httpStatus.forbidden.message", errorResponse?.stackTrace)
					});
				} else if (jqXHR.status === 401 || jqXHR.status === 403 && method === "POST") {
					// If the user is unauthorized (e.g. session timeout) and does a post request, the response is 403 instead
					// of 401 because of the invalid csfr token. We handle this case like a 401 unauthorized response.
					BootstrapModal.error({
						title: "httpStatus.unauthorized.title",
						message: ApiUrl.buildErrorDialogMessage("httpStatus.unauthorized.message", errorResponse?.stackTrace),
						buttons: [{
							text: "httpStatus.unauthorized.login",
							css: "btn-primary",
							action: () => {
								window.location.href = "/timr/";
							}
						}],
					});
				} else if (errorResponse?.message != null) {
					BootstrapModal.error({
						// DEVNOTE there are messages containing custom user strings, so they need to be escaped (escapeText is true by default but to make it more visible, see TIMR-8290)
						message: ApiUrl.buildErrorDialogMessage(errorResponse.message, errorResponse?.stackTrace)
					});
				} else if (jqXHR.status === 500) {
					// Only handle Internal Server Errors here, Bad Requests are handled individually
					BootstrapModal.error({
						message: ApiUrl.buildErrorDialogMessage("dialog.error.unknown", errorResponse?.stackTrace)
					});
				}
			},
			success: function (responseData, textStatus, jqXHR) {
				if (jqXHR != null) {
					// Fallback handling for non-json endpoints where we also check for the login page on success as some
					// endpoints return 200 OK with the login page HTML on session timeout instead of a 403 Forbidden.
					// Could potentially be simplified in the future if all endpoints are migrated to return JSON
					// and use proper status codes.
					if (ApiUrl.isLoginPage(jqXHR)) {
						window.location.href = "/timr/";
						return false;
					}

					ApiUrl.hideNetworkErrorModalIfNecessary(jqXHR);
				}
			}
		}, this.settings);

		return $.ajax(ajaxSettings);
	}

	public static isLoginPage(xhr: { responseText: string }) {
		return xhr.responseText && xhr.responseText.indexOf("LOGIN_PAGE_IDENTIFIER") !== -1;
	}

	private static hideNetworkErrorModalIfNecessary(jqXHR: JQueryXHR) {
		if (jqXHR.status !== 0 && ApiUrl.networkErrorModal != null) {
			// If we have a networkErrorModal and get a status that is not 0
			// we have a working network again and can hide the modal
			ApiUrl.networkErrorModal.close();
		}
	}

	private static showNetworkErrorModal(background: boolean = false) {
		if (!background) {
			if (ApiUrl.networkErrorModal == null) {
				ApiUrl.networkErrorModal = BootstrapModal.error({
					message: "network.error"
				});
			} else {
				ApiUrl.networkErrorModal.show();
			}
		}
	}

	private static buildErrorDialogMessage(messageKey: string, stackTrace?: string): JQuery {
		const localizedMessage = messages[messageKey] || messageKey;
		const element = $("<div>")
			.append($("<p>")
				.text(localizedMessage)
			);

		if (stackTrace) {
			const pre = $("<pre>").height(500).text(stackTrace);
			element.append(pre);
			enhanceCopyable(pre[0], messages["tooltip.copy"]);
		}

		return element;
	}
}
